import React, { useEffect, useRef, useState } from "react";
import { Building2, Check, ChevronDown, Plus, Loader2 } from "lucide-react";
import { useCompanies } from "@/context/CompanyContext";
import { Link } from "react-router-dom";

/**
 * Active-company picker for the top right of the app shell.
 *
 * The chosen company scopes documents, numbering and dashboard totals, so it is
 * deliberately global rather than per page. Selecting one calls the backend and
 * only then updates local state, which keeps the UI from showing a company the
 * server has not switched to.
 */
export default function CompanySwitcher() {
  const {
    companies,
    activeCompany,
    switchCompany,
    canAddCompany,
    used,
    limit,
    loading,
  } = useCompanies();

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  // Close on outside click / Escape so the menu behaves like a native popover.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // No profiles yet: the switcher would be a dead control, so link to onboarding.
  if (loading) return null;
  if (!companies.length) {
    return (
      <Link
        to="/app/companies"
        className="inline-flex items-center gap-2 px-3 h-9 border border-[var(--border)] rounded-md text-sm hover:bg-gray-50"
        data-testid="company-switcher-empty"
      >
        <Plus className="w-4 h-4" />
        Set up company
      </Link>
    );
  }

  const pick = async (id) => {
    setOpen(false);
    if (id === activeCompany?.id) return;
    setBusy(true);
    try {
      await switchCompany(id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 pl-2.5 pr-2.5 h-9 border border-[var(--border)] rounded-md bg-white hover:bg-gray-50 text-sm min-w-0 max-w-[260px]"
        data-testid="company-switcher"
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Switch the active company"
      >
        <Building2 className="w-4 h-4 shrink-0 text-[var(--primary)]" />
        <span className="truncate font-medium" data-testid="company-switcher-current">
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
          ) : (
            activeCompany?.name || "Unnamed company"
          )}
        </span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0 text-gray-400" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-50 w-72 bg-white border border-[var(--border)] rounded-lg shadow-lg overflow-hidden"
          data-testid="company-switcher-menu"
          role="listbox"
        >
          <div className="px-3 py-2 border-b border-[var(--border)] flex items-center justify-between">
            <span className="overline">Company</span>
            <span className="text-[11px] text-gray-500 num">
              {used} of {limit}
            </span>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {companies.map((c) => {
              const isActive = c.id === activeCompany?.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => pick(c.id)}
                  className="w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-start gap-2.5 border-b border-[var(--border)] last:border-0"
                  data-testid={`company-option-${c.id}`}
                >
                  <span className="mt-0.5 w-4 h-4 shrink-0 grid place-items-center">
                    {isActive && <Check className="w-4 h-4 text-[var(--primary)]" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium truncate">
                      {c.name || "Unnamed company"}
                    </span>
                    <span className="block text-[11px] text-gray-500 truncate">
                      {c.label ? `${c.label} · ` : ""}
                      {c.gstin || "No GSTIN"}
                      {typeof c.doc_count === "number" ? ` · ${c.doc_count} docs` : ""}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="p-2 border-t border-[var(--border)] bg-gray-50/60">
            <Link
              to="/app/companies"
              className="block text-center text-xs px-3 h-8 leading-8 border border-[var(--border)] rounded-md bg-white hover:bg-gray-50"
              data-testid="company-manage-link"
            >
              {canAddCompany ? "Manage companies" : "Manage companies (limit reached)"}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}